import {
  BadgeCell,
  type BadgeCellComponentProps,
  Column,
  type FilterItems,
  type ResetButtonFilterItemConfigProps,
} from '@odin-labs/components';
import {
  ConnectorRunStatus,
  GetConnectorRunsQuery,
} from 'apollo/generated/client-operations';
import { CalendarXmarkIcon } from 'components/icons';
import type { Moment } from 'moment';
import React from 'react';
import { getFriendlyFormattedDateTime } from 'utils/dates';

export type ConnectorRunListItem = NonNullable<
  GetConnectorRunsQuery['connector']
>['runs']['runs'][number];
export type ConnectorRunColumn = Column<ConnectorRunListItem>;

export type ConnectorRunFilters = {
  statuses: ConnectorRunStatus[] | null;
  dateRange: {
    startDate: Moment | null;
    endDate: Moment | null;
  } | null;
};

type ConnectorRunFilterItems = ConnectorRunFilters & {
  clearDate?: never;
};

type ConnectorRunStatusBadge = BadgeCellComponentProps & { text: string };

const runStatusBadges: Record<ConnectorRunStatus, ConnectorRunStatusBadge> = {
  [ConnectorRunStatus.Running]: { color: 'gray', text: 'Running' },
  [ConnectorRunStatus.Succeeded]: { color: 'green', text: 'Succeeded' },
  [ConnectorRunStatus.Failed]: { color: 'red', text: 'Failed' },
};
const unknownRunStatusBadge: ConnectorRunStatusBadge = {
  color: 'gray',
  text: 'Unknown',
};

const getRunStatusBadge = (
  status: ConnectorRunStatus,
): ConnectorRunStatusBadge => runStatusBadges[status] ?? unknownRunStatusBadge;

const runStatusOptions = Object.entries(runStatusBadges).map(
  ([status, { text: label }]) => ({
    value: status as ConnectorRunStatus,
    label,
  }),
);

export const getRunFilterItems = (): FilterItems<ConnectorRunFilterItems> => ({
  statuses: {
    header: 'Status',
    type: 'dropdown',
    defaultValue: null,
    componentProps: {
      options: runStatusOptions,
      isMulti: true,
      allowSearch: false,
      isNotSorted: true,
    },
  },
  dateRange: {
    header: 'Date',
    type: 'datePicker',
    defaultValue: null,
    layout: 'odin-col-span-full',
    componentProps: {
      isRange: true,
    },
  },
  clearDate: {
    header: 'Clear date',
    type: 'resetButton',
    isHidden: (filterValues: ConnectorRunFilters): boolean =>
      !filterValues.dateRange?.startDate && !filterValues.dateRange?.endDate,
    componentProps:
      (): ResetButtonFilterItemConfigProps<ConnectorRunFilters> => ({
        icon: CalendarXmarkIcon,
        filtersValues: { dateRange: null },
      }),
  },
});

export const getRunColumns = (): ConnectorRunColumn[] => [
  {
    id: 'status',
    Header: 'Status',
    accessor: (run): string => getRunStatusBadge(run.status).text,
    Cell: BadgeCell,
    componentProps: (run: ConnectorRunListItem): BadgeCellComponentProps =>
      getRunStatusBadge(run.status),
  },
  {
    id: 'message',
    Header: 'Details',
    accessor: (run): string => run.message ?? 'No message',
    Cell: ({
      value,
      row,
    }: {
      value: string;
      row: { original: ConnectorRunListItem };
    }): React.ReactElement => (
      <span className="odin-block">
        <span className="odin-block odin-text-sm odin-text-gray-900">
          {value}
        </span>
        <span className="odin-block odin-text-xs odin-text-gray-500">
          {getFriendlyFormattedDateTime(row.original.startedAt)}
        </span>
      </span>
    ),
  },
];
